<template>
	<div class="customer-area-page job-edit">
		<b-container>
			<b-row>
				<b-col md="3">
					<Navigation page="job-profiles"/>
				</b-col>
				<b-col md="9">
					<div class="sticky-title" v-sticky>
						<h1>
							{{ jobProfileName }}
							<a v-if="internal" href="#" class="ml-2" @click.prevent="editName = true">
								<Icon icon="pencil" color="primary" class="ml-1"/> Ändern
							</a>
						</h1>
					</div>
					<template v-if="showReviewIntro && !internal">
						<div class="job-bot">
							<JonMessages :messages="reviewIntroMessages"/>
							<div class="button-container">
								<Button
									text="Los geht's"
									icon="checkmark"
									:style="{'animation-delay': (reviewIntroMessages.length) + 's'}"
									@click="showReviewIntro = false"
								/>
							</div>
						</div>
					</template>
					<div v-else class="main-content profile-view">
						<div :class="['tag-sections', {'loading-blockade': reloadingJobProfile || saving}]">
							<section
								v-for="item in groupedItems"
								:key="item.area.id"
							>
								<Title
									:title="item.area.label"
									parent
									:editable="false"
									:completion-type="getGroupCompletionType(item)"
								/>
								<div v-if="jobProfileData" class="sub-sections">
									<div v-if="getNonEditableItems(item.area).length > 0" class="non-editable-items">
										<div class="non-editable-items--items">
											<div v-for="nonEditableItem in getNonEditableItems(item.area)"
												 class="non-editable-items--item">
												<b-row>
													<b-col md="4">
														<div class="non-editable-items--item-label">
															<a
																v-if="inReview && !confirming"
																href="#"
																class="color-default no-hover-color"
																@click.prevent="edit(nonEditableItem.inquiry)"
															>
																{{ getInquiryLabel(nonEditableItem.inquiry) }}
															</a>
															<template v-else>
																{{ getInquiryLabel(nonEditableItem.inquiry) }}
															</template>
														</div>
													</b-col>
													<b-col md="8">
														<div class="non-editable-items--item-values">
															<div v-if="inReview && !isInquiryCompleted(nonEditableItem.inquiry)">
																<i class="color-primary">Bitte gib hier noch die Infos ein.</i>
															</div>
															<template v-else>
																<div
																	v-for="valueItem in filterLastLevel(nonEditableItem.config.dataProvider({jobProfileData}))"
																>
																	<a
																		v-if="inReview && !confirming"
																		href="#"
																		class="color-default no-hover-color"
																		@click.prevent="edit(nonEditableItem.inquiry)"
																	>
																		<Label
																			:characteristic="valueItem.item"
																			:label-type="labelTypeJobProfile"
																			:supplements="valueItem.supplements"
																		/>
																	</a>
																	<Label
																		v-else
																		:characteristic="valueItem.item"
																		:label-type="labelTypeJobProfile"
																		:supplements="valueItem.supplements"
																	/>
																</div>
															</template>
															<div v-if="inReview && !confirming" class="mt-1">
																<a href="#" @click.prevent="edit(nonEditableItem.inquiry)">
																	<Icon icon="pencil" color="primary"/>
																	Bearbeiten
																</a>
															</div>
														</div>
													</b-col>
												</b-row>
											</div>
										</div>
										<LockInfo>
											<template v-if="inReview" #default>
												Diese Angaben zum Job sind im Nachhinein nicht mehr änderbar.<br/>
												Bitte ergänze und ändere daher die Angaben oben.
											</template>
										</LockInfo>
									</div>
									<div
										v-if="isInquiryEditable(inquiry)"
										v-for="inquiry in item.inquiries"
										:key="inquiry.id"
										class="sub-section"
									>
										<Title
											:title="getInquiryLabel(inquiry)"
											:editable="isInquiryEditable(inquiry)"
											:completion-type="getInquiryCompletionType(inquiry)"
											@edit="edit(inquiry)"
										/>
										<template v-for="item in selectedItemsConfigs">
											<SelectedItems
												v-if="inquiry.id === item.inquiryId"
												:key="inquiry.id"
												:characteristic-items="meta[item.config.metaItemsKey]"
												:characteristic-type="meta[item.config.metaItemsTypeKey]"
												:selected="item.config.filterLastLevel !== false
													? filterLastLevel(jobProfileData[item.config.dataProperty])
													: jobProfileData[item.config.dataProperty]"
												:label-type="labelTypeJobProfile"
												:show-additions="item.config.showAdditions === true"
												:show-experience="item.config.suppressExperience !== true"
												:flat="item.config.flat !== false"
												:none-info="item.config.noneInfo || 'Nein'"
												:styling="!item.config.noListStyling ? selectedItemsStylingList : undefined"
												:class="{'inline': isSelectedItemsInline(item.config, jobProfileData[item.config.dataProperty])}"
												:specificities-provider="item.config.specificitiesProvider"
												:icon-path="item.config.iconPath"
												:clickable="isInquiryEditable(inquiry)"
												@editItem="edit(inquiry)"
											/>
										</template>
										<FunctionDetail
											v-if="inquiry.id === 'function-detail'"
											:data="jobProfileData.functionDetail"
											@click="edit(inquiry)"
										/>
										<PlaceOfWork
											v-if="inquiry.id === 'place-of-work'"
											:data="jobProfileData.placeOfWork"
											@click="edit(inquiry)"
										/>
										<HiringDate
											v-if="inquiry.id === 'hiring-date'"
											:data="jobProfileData.hiringDate"
											@click="edit(inquiry)"
										/>
										<WorkingHours
											v-if="inquiry.id === 'working-hours'"
											:data="jobProfileData.workingHours"
											@click="edit(inquiry)"
										/>
										<EmploymentDuration
											v-if="inquiry.id === 'employment-duration'"
											:data="jobProfileData.employmentDuration"
											@click="edit(inquiry)"
										/>
										<Salary
											v-if="inquiry.id === 'salary'"
											:data="jobProfileData.salary"
											@click="edit(inquiry)"
										/>
										<HourlyRate
											v-if="inquiry.id === 'hourly-rate'"
											:data="jobProfileData.hourlyRate"
											@click="edit(inquiry)"
										/>
										<TravelWillingness
											v-if="inquiry.id === 'travel-willingness'"
											:data="jobProfileData.travelWillingness"
											@click="edit(inquiry)"
										/>
										<Education
											v-if="inquiry.id === 'education'"
											:data="jobProfileData.education"
											@click="edit(inquiry)"
										/>
										<HomeOffice
											v-if="inquiry.id === 'home-office'"
											:data="jobProfileData.homeOffice"
											@click="edit(inquiry)"
										/>
										<IncognitoMode
											v-if="inquiry.id === 'incognito-mode'"
											:data="jobProfileData.incognitoMode"
											@click="edit(inquiry)"
										/>
									</div>
									<div v-if="inReview && !internal" class="mt-4">
										<Button
											text="Bestätigen"
											icon="checkmark"
											:disabled="confirming || !canConfirm"
											:loading="confirming"
											@click="confirm"
										/>
									</div>
								</div>
							</section>
						</div>
					</div>
				</b-col>
			</b-row>
		</b-container>
		<PopUp
			v-if="editName"
			title="Name bearbeiten"
			ok-text="Speichern"
			:ok-loading="changingName"
			:hide-cancel="changingName"
			:hide-close="changingName"
			:ok-disabled="!changeNameDataValid || changingName"
			@ok="saveName"
			@hidden="onAbortNameChange"
		>
			<Group label="Name">
				<b-input v-model="jobProfileNameToEdit"/>
			</Group>
			<div class="mt-4">
				<b>ACHTUNG:</b> dieser Jobtitel wird den Kandidaten angezeigt und darf daher
				keine Hinweise auf deine Identität und Organisation beinhalten.
			</div>
		</PopUp>
	</div>
</template>

<script>
import Navigation from "@/vue/customer-area/Navigation"
import _ from "lodash";
import Icon from "@/vue/icon/Icon.vue";
import Title, {COMPLETION_TYPE__COMPLETED, COMPLETION_TYPE__INCOMPLETE} from "@/vue/common/profile-view/Title.vue";
import * as Inquiry from "@/helper/job-profile/Inquiry";
import {mapActions, mapState} from "vuex";
import {prepare} from "@/helper/job-profile/PreFillPreparation";
import SelectedItems, {STYLING__LIST} from "@/vue/characteristic/SelectedItems";
import {filterLastLevel} from "@/helper/characteristic/Characteristic";
import {JOB_PROFILE} from "@/helper/characteristic/Label";
import {isDefined} from "@/helper/Value";
import PlaceOfWork from "./edit-display/PlaceOfWork"
import FunctionDetail from "./edit-display/FunctionDetail"
import HiringDate from "./edit-display/HiringDate"
import WorkingHours from "./edit-display/WorkingHours"
import EmploymentDuration from "./edit-display/EmploymentDuration"
import Salary from "./edit-display/Salary"
import TravelWillingness from "./edit-display/TravelWillingness"
import HourlyRate from "./edit-display/HourlyRate"
import Education from "./edit-display/Education"
import HomeOffice from "./edit-display/HomeOffice"
import {isEmptyArray, itemsCountIs} from "@/helper/Array";
import GetList from "@/api/customer/job-profile/GetList";
import {LANGUAGE_MOTHER} from "@/helper/job-profile/SpecificityType";
import PopUp from "@/vue/common/PopUp.vue";
import Group from "@/vue/common/form/Group.vue";
import {isEmptyOrNull} from "@/helper/String";
import * as Toast from "@/helper/layout/Toast";
import flatten from "@/helper/error/Errors";
import Save from "@/api/customer/job-profile/Save";
import JobProfileSave from "@/api/customer/job-profile/Save";
import IncognitoMode from "./edit-display/IncognitoMode";
import * as PreSavePreparation from "@/helper/job-profile/PreSavePreparation";
import * as Log from "@/helper/Log";
import {DEFAULT_EXPERIENCE_INFO} from "@/helper/job-profile/Experience";
import {RELEVANT_SUPPLEMENT_TYPES} from "@/helper/characteristic/Functions";
import LockInfo from "@/vue/common/LockInfo.vue";
import Label from "@/vue/characteristic/Label.vue";
import {EXTERNAL} from "@/helper/customer/Type";
import Button from "@/vue/common/Button.vue";
import {redirect} from "@/helper/Window";
import Jon from "@/vue/common/image/Jon.vue";
import JonMessages from "@/vue/customer-area/job-profile/JonMessages.vue";

const CONFIGS = {
	[Inquiry.FUNCTION]: {
		dataProvider: ({jobProfileData}) => jobProfileData.functions,
		editable: false,
		selectedItemsConfig: {
			metaItemsKey: 'function',
			metaItemsTypeKey: 'functionType',
			dataProperty: 'functions',
			showAdditions: true,
			suppressExperience: true
		}
	},
	[Inquiry.FUNCTION_DETAIL]: {
		fanTitle: 'Welche Tätigkeiten umfasst der Job?',
		fanSubTitle: 'Um das Interesse von potentiellen MitarbeiterInnen zu steigern, gib die Tätigkeiten möglichst detailliert an!<br/><b>ACHTUNG:</b> Dieser Text ist für Kandidaten sichtbar. Achte darauf, dass du geheim bleibst!',
		dataProvider: ({jobProfileData}) =>
		{
			return jobProfileData.functionDetail;
		}
	},
	[Inquiry.FUNCTION_ASSISTANCE]: {
		dataProvider: ({jobProfileData}) => jobProfileData.functionAssistance,
		selectedItemsConfig: {
			metaItemsKey: 'functionAssistance',
			metaItemsTypeKey: 'functionAssistanceType',
			dataProperty: 'functionAssistance',
			noneInfo: 'Keine Auswahl'
		}
	},
	[Inquiry.FUNCTION_LEGAL_FIELD]: {
		dataProvider: ({jobProfileData}) => jobProfileData.functionLegalField,
		selectedItemsConfig: {
			metaItemsKey: 'functionLegalField',
			metaItemsTypeKey: 'functionLegalFieldType',
			dataProperty: 'functionLegalField',
			noneInfo: 'Keine Auswahl'
		},
		characteristicEditFanConfigProvider: ({meta}) =>
		{
			return {
				property: 'functionLegalField',
				characteristicType: meta.functionLegalFieldType,
				characteristicItems: meta.functionLegalField,
				title: 'Welche Rechtsgebiete umfasst der Job?',
				subTitle: 'Um das Interesse von potentiellen MitarbeiterInnen zu steigern, gib die Tätigkeiten möglichst detailliert an!',
				supplementsConfig: {
					disable: true
				},
				nothingAllowed: {
					onlyOverall: true,
					overall: {
						inFan: true,
						text: 'Keine der zur Auswahl stehenden'
					}
				},
				selectedItemsConfig: {
					showExperience: false,
				},
			};
		}
	},
	[Inquiry.FUNCTION_LEGAL_ROLE]: {
		fanTitle: 'Welche Rechtstätigkeiten umfasst der Job?',
		fanSubTitle: 'Um das Interesse von potentiellen MitarbeiterInnen zu steigern, gib die Tätigkeiten möglichst detailliert an!',
		dataProvider: ({jobProfileData}) => jobProfileData.functionLegalRole,
		selectedItemsConfig: {
			metaItemsKey: 'functionLegalRole',
			metaItemsTypeKey: 'functionLegalRoleType',
			dataProperty: 'functionLegalRole',
			noneInfo: 'Keine Auswahl'
		},
	},
	[Inquiry.FUNCTION_TAX_FIELD]: {
		dataProvider: ({jobProfileData}) => jobProfileData.functionTaxField,
		selectedItemsConfig: {
			metaItemsKey: 'functionTaxField',
			metaItemsTypeKey: 'functionTaxFieldType',
			dataProperty: 'functionTaxField',
			noneInfo: 'Keine Auswahl'
		},
		characteristicEditFanConfigProvider: ({meta}) =>
		{
			return {
				property: 'functionTaxField',
				characteristicType: meta.functionTaxFieldType,
				characteristicItems: meta.functionTaxField,
				title: 'Welche Steuergebiete umfasst der Job?',
				subTitle: 'Um das Interesse von potentiellen MitarbeiterInnen zu steigern, gib die Tätigkeiten möglichst detailliert an!',
				supplementsConfig: {
					disable: true
				},
				nothingAllowed: {
					onlyOverall: true,
					overall: {
						inFan: true,
						text: 'Keine der zur Auswahl stehenden'
					}
				},
				selectedItemsConfig: {
					showExperience: false,
				},
			};
		}
	},
	[Inquiry.FUNCTION_TAX_ROLE]: {
		dataProvider: ({jobProfileData}) => jobProfileData.functionTaxRole,
		selectedItemsConfig: {
			metaItemsKey: 'functionTaxRole',
			metaItemsTypeKey: 'functionTaxRoleType',
			dataProperty: 'functionTaxRole',
			noneInfo: 'Keine Auswahl'
		}
	},
	[Inquiry.FUNCTION_MARKETING_ROLE]: {
		dataProvider: ({jobProfileData}) => jobProfileData.functionMarketingRole,
		selectedItemsConfig: {
			metaItemsKey: 'functionMarketingRole',
			metaItemsTypeKey: 'functionMarketingRoleType',
			dataProperty: 'functionMarketingRole',
			noneInfo: 'Keine Auswahl'
		}
	},
	[Inquiry.CAREER_LEVEL]: {
		dataProvider: ({jobProfileData}) => jobProfileData.careerLevels,
		editable: false,
		selectedItemsConfig: {
			metaItemsKey: 'careerLevel',
			metaItemsTypeKey: 'careerLevelType',
			dataProperty: 'careerLevels'
		}
	},
	[Inquiry.INDUSTRIAL_SECTOR]: {
		dataProvider: ({jobProfileData}) => jobProfileData.industrialSectors,
		editable: false,
		selectedItemsConfig: {
			metaItemsKey: 'industrialSector',
			metaItemsTypeKey: 'industrialSectorType',
			dataProperty: 'industrialSectors'
		}
	},
	[Inquiry.ORGANISATIONAL_AREA]: {
		dataProvider: ({jobProfileData}) => jobProfileData.organisationalArea,
		editable: false,
		selectedItemsConfig: {
			metaItemsKey: 'organisationalArea',
			metaItemsTypeKey: 'organisationalAreaType',
			dataProperty: 'organisationalArea'
		}
	},
	[Inquiry.PLACE_OF_WORK]: {
		fanTitle: 'Wo ist der Arbeitsort für diesen Job?',
		fanSubTitle: 'Mehrfachauswahl möglich, wenn unterschiedliche Arbeitsorte möglich sind.<br/>Wenn großteils Remote/Homeoffice vorgesehen ist, gib bitte Euren Hauptsitz für diesen Job ein.',
		dataProvider: ({jobProfileData}) => jobProfileData.placeOfWork,
	},
	[Inquiry.COMPANY_TYPE]: {
		fanTitle: ({customer}) =>
		{
			return customer.type === EXTERNAL
				? 'Für welche Organisationsart suchst du?'
				: 'Welche Organisationsart habt ihr?';
		},
		dataProvider: ({jobProfileData}) => jobProfileData.companyType,
		selectedItemsConfig: {
			metaItemsKey: 'companyType',
			metaItemsTypeKey: 'companyTypeType',
			dataProperty: 'companyType'
		}
	},
	[Inquiry.COMPANY_SIZE]: {
		fanTitle: ({customer}) =>
		{
			return customer.type === EXTERNAL
				? 'Wie groß ist die Organisation, für die du suchst?'
				: 'Wie groß ist eure Organisation?';
		},
		dataProvider: ({jobProfileData}) => jobProfileData.companySize,
		selectedItemsConfig: {
			metaItemsKey: 'companySize',
			metaItemsTypeKey: 'companySizeType',
			dataProperty: 'companySize'
		}
	},
	[Inquiry.WORKING_TIME]: {
		dataProvider: ({jobProfileData}) => jobProfileData.workingTime,
		fanTitle: 'Sind spezielle Arbeitszeiten erforderlich?',
		selectedItemsConfig: {
			metaItemsKey: 'workingTime',
			metaItemsTypeKey: 'workingTimeType',
			dataProperty: 'workingTime',
			noneInfo: 'Keine'
		}
	},
	[Inquiry.EMPLOYMENT_MODE]: {
		fanTitle: 'Welche Job-Art ist das?',
		dataProvider: ({jobProfileData}) => jobProfileData.employmentMode,
		selectedItemsConfig: {
			metaItemsKey: 'employmentMode',
			metaItemsTypeKey: 'employmentModeType',
			dataProperty: 'employmentMode'
		}
	},
	[Inquiry.MANAGEMENT]: {
		fanTitle: 'Gibt es in dem Job Führungsverantwortung?',
		dataProvider: ({jobProfileData}) => jobProfileData.management,
		selectedItemsConfig: {
			metaItemsKey: 'managementKind',
			metaItemsTypeKey: 'managementKindType',
			dataProperty: 'management',
			noneInfo: 'Keine',
			showAdditions: true,
			inlineProvider: (data) => isEmptyArray(data)
		}
	},
	[Inquiry.CUSTOMER_CONTACT]: {
		fanTitle: 'Hat man Kundenkontakt in dem Job?',
		dataProvider: ({jobProfileData}) => jobProfileData.customerContact,
		selectedItemsConfig: {
			metaItemsKey: 'businessModel',
			metaItemsTypeKey: 'businessModelType',
			dataProperty: 'customerContact',
			noneInfo: 'Keiner',
			showAdditions: true,
			inlineProvider: (data) => isEmptyArray(data)
		}
	},
	[Inquiry.PUBLIC_TRANSPORT]: {
		fanTitle: 'Wie gut ist die Erreichbarkeit des Arbeitsplatzes mit öffentlichen Verkehrsmitteln?',
		fanSubTitle: 'Auch wenn 100% Homeoffice/Remote möglich ist, gib es bitte an für Personen, die zu euch kommen wollen.',
		dataProvider: ({jobProfileData}) => jobProfileData.publicTransport,
		selectedItemsConfig: {
			metaItemsKey: 'publicTransport',
			metaItemsTypeKey: 'publicTransportType',
			dataProperty: 'publicTransport',
		}
	},
	[Inquiry.PARKING]: {
		fanTitle: 'Gibt es einen Parkplatz?',
		dataProvider: ({jobProfileData}) => jobProfileData.parking,
		selectedItemsConfig: {
			metaItemsKey: 'parking',
			metaItemsTypeKey: 'parkingType',
			dataProperty: 'parking',
		}
	},
	[Inquiry.OVERTIME]: {
		fanTitle: 'Sind Überstunden bei dem Job vorgesehen?',
		dataProvider: ({jobProfileData}) => jobProfileData.overtime,
		selectedItemsConfig: {
			metaItemsKey: 'overtime',
			metaItemsTypeKey: 'overtimeType',
			dataProperty: 'overtime',
			noneInfo: 'Keine'
		}
	},
	[Inquiry.DRIVING_LICENSE]: {
		fanTitle: 'Wird ein Führerschein benötigt?',
		dataProvider: ({jobProfileData}) => jobProfileData.drivingLicense,
		selectedItemsConfig: {
			metaItemsKey: 'drivingLicenseClass',
			metaItemsTypeKey: 'drivingLicenseClassType',
			dataProperty: 'drivingLicense',
			noneInfo: 'Kein Führerschein notwendig',
			iconPath: '/img/characteristic/driving-license'
		}
	},
	[Inquiry.PILOT_LICENSE]: {
		fanTitle: 'Wird ein Flugschein benötigt?',
		dataProvider: ({jobProfileData}) => jobProfileData.pilotLicense,
		selectedItemsConfig: {
			metaItemsKey: 'pilotLicense',
			metaItemsTypeKey: 'pilotLicenseType',
			dataProperty: 'pilotLicense',
			noneInfo: 'Kein Flugschein notwendig'
		}
	},
	[Inquiry.HIRING_DATE]: {
		fanTitle: 'Wann soll dein neuer Mitarbeiter starten?',
		dataProvider: ({jobProfileData}) => jobProfileData.hiringDate
	},
	[Inquiry.HOME_OFFICE]: {
		fanTitle: 'Homeoffice, was ist möglich bei diesem Job?',
		dataProvider: ({jobProfileData}) => jobProfileData.homeOffice
	},
	[Inquiry.WORKING_HOURS]: {
		fanTitle: 'Bei Teilzeit: Für wie viele Stunden pro Woche sucht ihr jemanden?',
		dataProvider: ({jobProfileData}) => jobProfileData.workingHours
	},
	[Inquiry.EMPLOYMENT_DURATION]: {
		fanTitle: 'Wie lange ist die Beschäftigungsdauer?',
		dataProvider: ({jobProfileData}) => jobProfileData.employmentDuration
	},
	[Inquiry.SALARY]: {
		fanTitle: 'Wie viel kann man verdienen bei dem Job?',
		fanSubTitle: 'Das Gehalt wird den Bewerbern nicht direkt angezeigt, sondern nur verwendet, um passende Übereinstimmungen zu finden.',
		dataProvider: ({jobProfileData}) => jobProfileData.salary
	},
	[Inquiry.HOURLY_RATE]: {
		fanTitle: 'Welcher Stundensatz ist möglich?',
		dataProvider: ({jobProfileData}) => jobProfileData.hourlyRate
	},
	[Inquiry.TRAVEL_WILLINGNESS]: {
		fanTitle: 'Ist Außendienst bei dem Job vorgesehen?',
		dataProvider: ({jobProfileData}) => jobProfileData.travelWillingness
	},
	[Inquiry.PRIVATE_VEHICLE_COMPANY_USAGE]: {
		fanTitle: 'Wird ein Privatauto benötigt für den Berufseinsatz?',
		dataProvider: ({jobProfileData}) => jobProfileData.privateVehicleCompanyUsage,
		selectedItemsConfig: {
			metaItemsKey: 'privateVehicleCompanyUsage',
			metaItemsTypeKey: 'privateVehicleCompanyUsageType',
			dataProperty: 'privateVehicleCompanyUsage',
			noneInfo: 'Nein',
		}
	},
	[Inquiry.COMPANY_CAR]: {
		fanTitle: 'Bekommt man ein Firmenauto?',
		dataProvider: ({jobProfileData}) => jobProfileData.companyCar,
		selectedItemsConfig: {
			metaItemsKey: 'companyCar',
			metaItemsTypeKey: 'companyCarType',
			dataProperty: 'companyCar',
			noneInfo: 'Nein',
		}
	},
	[Inquiry.FUNCTION_EXPERIENCE]: {
		dataProvider: ({jobProfileData}) => jobProfileData.functionsExperience,
		selectedItemsConfig: {
			metaItemsKey: 'function',
			metaItemsTypeKey: 'functionType',
			dataProperty: 'functionsExperience',
			noneInfo: 'Keine Vorerfahrung nötig',
			showAdditions: true,
			inlineProvider: (data) => isEmptyArray(data)
		},
		characteristicEditFanConfigProvider: ({meta}) =>
		{
			return {
				property: 'functionsExperience',
				characteristicType: meta.functionType,
				characteristicItems: meta.function,
				title: 'Sollte man bestimmte Erfahrungen mitbringen für die Tätigkeit?',
				subTitle: DEFAULT_EXPERIENCE_INFO,
				nothingAllowed: {
					onlyOverall: true,
					overall: {
						inFan: true,
						text: 'Es ist keine Tätigkeits-Vorerfahrung nötig'
					}
				},
				experienceConfig: {
					noneAllowed: false
				},
				selectedItemsConfig: {
					labelType: JOB_PROFILE
				},
				additionsConfig: {
					type: 'customer'
				},
				withKeyword: true,
				searchConfig: {catalog: meta.searchCatalogFunction},
				withExperience: true,
				supplementsConfig: {
					nothingAllowed: true,
					types: RELEVANT_SUPPLEMENT_TYPES,
				}
			};
		}
	},
	[Inquiry.CAREER_LEVEL_EXPERIENCE]: {
		dataProvider: ({jobProfileData}) => jobProfileData.careerLevelsExperience,
		selectedItemsConfig: {
			metaItemsKey: 'careerLevel',
			metaItemsTypeKey: 'careerLevelType',
			dataProperty: 'careerLevelsExperience',
			noneInfo: 'Keine Vorerfahrung nötig',
			showAdditions: true,
			inlineProvider: (data) => isEmptyArray(data)
		},
		characteristicEditFanConfigProvider: ({meta}) =>
		{
			return {
				property: 'careerLevelsExperience',
				characteristicType: meta.careerLevelType,
				characteristicItems: meta.careerLevel,
				title: 'Ist Arbeitserfahrung erforderlich, wie zum Beispiel Praktikum, Lehre, als Mitarbeiter, Spezialist oder in Projektleitung, Teamleitung, Management usw.?',
				subTitle: DEFAULT_EXPERIENCE_INFO,
				nothingAllowed: {
					onlyOverall: true,
					overall: {
						inFan: true,
						text: 'Es ist keine Positions-Vorerfahrung nötig'
					}
				},
				experienceConfig: {
					noneAllowed: false
				},
				selectedItemsConfig: {
					labelType: JOB_PROFILE
				},
				additionsConfig: {
					type: 'customer'
				},
				withExperience: true,
			};
		}
	},
	[Inquiry.INDUSTRIAL_SECTOR_EXPERIENCE]: {
		dataProvider: ({jobProfileData}) => jobProfileData.industrialSectorsExperience,
		selectedItemsConfig: {
			metaItemsKey: 'industrialSector',
			metaItemsTypeKey: 'industrialSectorType',
			dataProperty: 'industrialSectorsExperience',
			noneInfo: 'Keine Vorerfahrung nötig',
			showAdditions: true,
			inlineProvider: (data) => isEmptyArray(data)
		},
		characteristicEditFanConfigProvider: ({meta}) =>
		{
			return {
				property: 'industrialSectorsExperience',
				characteristicType: meta.industrialSectorType,
				characteristicItems: meta.industrialSector,
				title: 'Ist Erfahrung in bestimmten Branchen erforderlich?',
				subTitle: DEFAULT_EXPERIENCE_INFO,
				nothingAllowed: {
					onlyOverall: true,
					overall: {
						inFan: true,
						text: 'Es ist keine Branchen-Vorerfahrung nötig'
					}
				},
				experienceConfig: {
					noneAllowed: false
				},
				selectedItemsConfig: {
					labelType: JOB_PROFILE
				},
				additionsConfig: {
					type: 'customer'
				},
				withExperience: true,
				withKeyword: true,
				searchConfig: {catalog: meta.searchCatalogIndustrialSector}
			};
		}
	},
	[Inquiry.ORGANISATIONAL_AREA_EXPERIENCE]: {
		dataProvider: ({jobProfileData}) => jobProfileData.organisationalAreaExperience,
		selectedItemsConfig: {
			metaItemsKey: 'organisationalArea',
			metaItemsTypeKey: 'organisationalAreaType',
			dataProperty: 'organisationalAreaExperience',
			noneInfo: 'Keine Vorerfahrung nötig',
			showAdditions: true,
			inlineProvider: (data) => isEmptyArray(data)
		},
		characteristicEditFanConfigProvider: ({meta}) =>
		{
			return {
				property: 'organisationalAreaExperience',
				characteristicType: meta.organisationalAreaType,
				characteristicItems: meta.organisationalArea,
				title: 'Ist Erfahrung in bestimmten Organisationsbereichen erforderlich?',
				subTitle: DEFAULT_EXPERIENCE_INFO,
				nothingAllowed: {
					onlyOverall: true,
					overall: {
						inFan: true,
						text: 'Es ist keine Organisationsbereich-Vorerfahrung nötig'
					}
				},
				experienceConfig: {
					noneAllowed: false
				},
				selectedItemsConfig: {
					labelType: JOB_PROFILE
				},
				additionsConfig: {
					type: 'customer'
				},
				withExperience: true,
				withKeyword: true,
			};
		}
	},
	[Inquiry.FUNCTION_LEGAL_FIELD_EXPERIENCE]: {
		dataProvider: ({jobProfileData}) => jobProfileData.functionLegalFieldExperience,
		selectedItemsConfig: {
			metaItemsKey: 'functionLegalField',
			metaItemsTypeKey: 'functionLegalFieldType',
			dataProperty: 'functionLegalFieldExperience',
			noneInfo: 'Keine Vorerfahrung nötig',
			showAdditions: true,
			inlineProvider: (data) => isEmptyArray(data)
		},
		characteristicEditFanConfigProvider: ({meta}) =>
		{
			return {
				property: 'functionLegalFieldExperience',
				characteristicType: meta.functionLegalFieldType,
				characteristicItems: meta.functionLegalField,
				title: 'Wird Erfahrung in bestimmten Rechtsgebieten vorausgesetzt?',
				subTitle: DEFAULT_EXPERIENCE_INFO,
				supplementsConfig: {
					disable: true
				},
				nothingAllowed: {
					onlyOverall: true,
					overall: {
						inFan: true,
						text: 'Es ist keine Vorerfahrung nötig'
					}
				},
				experienceConfig: {
					noneAllowed: false
				},
				selectedItemsConfig: {
					labelType: JOB_PROFILE
				},
				additionsConfig: {
					type: 'customer'
				},
				withExperience: true,
			};
		}
	},
	[Inquiry.FUNCTION_LEGAL_ROLE_EXPERIENCE]: {
		dataProvider: ({jobProfileData}) => jobProfileData.functionLegalRoleExperience,
		selectedItemsConfig: {
			metaItemsKey: 'functionLegalRole',
			metaItemsTypeKey: 'functionLegalRoleType',
			dataProperty: 'functionLegalRoleExperience',
			noneInfo: 'Keine Vorerfahrung nötig',
			suppressExperience: true,
			showAdditions: true,
			inlineProvider: (data) => isEmptyArray(data)
		}
	},
	[Inquiry.FUNCTION_TAX_FIELD_EXPERIENCE]: {
		dataProvider: ({jobProfileData}) => jobProfileData.functionTaxFieldExperience,
		selectedItemsConfig: {
			metaItemsKey: 'functionTaxField',
			metaItemsTypeKey: 'functionTaxFieldType',
			dataProperty: 'functionTaxFieldExperience',
			noneInfo: 'Keine Vorerfahrung nötig',
			showAdditions: true,
			inlineProvider: (data) => isEmptyArray(data)
		},
		characteristicEditFanConfigProvider: ({meta}) =>
		{
			return {
				property: 'functionTaxFieldExperience',
				characteristicType: meta.functionTaxFieldType,
				characteristicItems: meta.functionTaxField,
				title: 'Wird Erfahrung in bestimmten Steuergebieten vorausgesetzt?',
				subTitle: DEFAULT_EXPERIENCE_INFO,
				supplementsConfig: {
					disable: true
				},
				nothingAllowed: {
					onlyOverall: true,
					overall: {
						inFan: true,
						text: 'Es ist keine Vorerfahrung nötig'
					}
				},
				experienceConfig: {
					noneAllowed: false
				},
				selectedItemsConfig: {
					labelType: JOB_PROFILE
				},
				additionsConfig: {
					type: 'customer'
				},
				withExperience: true,
			};
		}
	},
	[Inquiry.FUNCTION_TAX_ROLE_EXPERIENCE]: {
		dataProvider: ({jobProfileData}) => jobProfileData.functionTaxRoleExperience,
		selectedItemsConfig: {
			metaItemsKey: 'functionTaxRole',
			metaItemsTypeKey: 'functionTaxRoleType',
			dataProperty: 'functionTaxRoleExperience',
			noneInfo: 'Keine Vorerfahrung nötig',
			suppressExperience: true,
			showAdditions: true,
			inlineProvider: (data) => isEmptyArray(data)
		}
	},
	[Inquiry.FUNCTION_MARKETING_ROLE_EXPERIENCE]: {
		dataProvider: ({jobProfileData}) => jobProfileData.functionMarketingRoleExperience,
		selectedItemsConfig: {
			metaItemsKey: 'functionMarketingRole',
			metaItemsTypeKey: 'functionMarketingRoleType',
			dataProperty: 'functionMarketingRoleExperience',
			noneInfo: 'Keine Vorerfahrung nötig',
			suppressExperience: true,
			showAdditions: true,
			inlineProvider: (data) => isEmptyArray(data)
		}
	},
	[Inquiry.SOFTWARE_MAIN]: {
		fanTitle: 'Welche Büro-Software muss man bei diesem Job anwenden?',
		fanSubTitle: 'Wichtig: lege nur Muss-Anforderungen fest, da sonst viele Bewerber abgeschreckt werden bzw. ausscheiden.',
		dataProvider: ({jobProfileData}) => jobProfileData.softwareMain,
		selectedItemsConfig: {
			metaItemsKey: 'skillSoftwareMain',
			metaItemsTypeKey: 'skillSoftwareMainType',
			dataProperty: 'softwareMain',
			noneInfo: 'Keine Kenntnisse nötig',
			suppressExperience: true,
			showAdditions: true,
			inlineProvider: (data) => isEmptyArray(data)
		}
	},
	[Inquiry.BENEFITS]: {
		dataProvider: ({jobProfileData}) => jobProfileData.benefits,
		selectedItemsConfig: {
			metaItemsKey: 'benefits',
			metaItemsTypeKey: 'benefitsType',
			dataProperty: 'benefits',
			noneInfo: 'Keine',
			inlineProvider: (data) => isEmptyArray(data)
		},
		characteristicEditFanConfigProvider: ({meta}) =>
		{
			return {
				property: 'benefits',
				characteristicType: meta.benefitsType,
				characteristicItems: meta.benefits,
				title: 'Benefits',
				subTitle: 'Welche Zusatzangebote und Vorteile bietet ihr bei diesem Job an?',
				selectedItemsConfig: {
					showExperience: false
				},
				nothingAllowed: {
					onlyOverall: true,
					overall: {
						inFan: true,
						text: 'Bei diesem Job gibt es keine Benefits'
					}
				}
			};
		}
	},
	[Inquiry.SKILLS]: {
		dataProvider: ({jobProfileData}) => jobProfileData.skills,
		selectedItemsConfig: {
			metaItemsKey: 'skill',
			metaItemsTypeKey: 'skillType',
			dataProperty: 'skills',
			noneInfo: 'Keine nötig',
			inlineProvider: (data) => isEmptyArray(data)
		}
	},
	[Inquiry.SOFTWARE_SPECIAL]: {
		fanTitle: 'Welche weitere Software muss man anwenden?',
		fanSubTitle: 'Zum Beispiel Programme für Grafik, Programmiersprachen, Buchhaltung, Musikbearbeitung, Konstruktion, Design, Produktion, Logistik und alle anderen. - Zum Beispiel BMD, SAP FI, Adobe Premiere, AutoCAD, Java, AutoCad.<br/>Nicht gemeint ist damit Bürosoftware für Textverarbeitung, Tabellenkalkulation, Mail/Termin oder Präsentation',
		dataProvider: ({jobProfileData}) => jobProfileData.softwareSpecial,
		selectedItemsConfig: {
			metaItemsKey: 'skillSoftwareSpecial',
			metaItemsTypeKey: 'skillSoftwareSpecialType',
			dataProperty: 'softwareSpecial',
			noneInfo: 'Keine Kenntnisse nötig',
			suppressExperience: true,
			showAdditions: true,
			inlineProvider: (data) => isEmptyArray(data)
		}
	},
	[Inquiry.LANGUAGE]: {
		fanTitle: 'Welche Sprachen sollte man beherrschen?',
		dataProvider: ({jobProfileData}) => jobProfileData.languages,
		selectedItemsConfig: {
			metaItemsKey: 'language',
			metaItemsTypeKey: 'languageType',
			dataProperty: 'languages',
			noneInfo: 'Keine Sprachkenntnisse nötig',
			suppressExperience: true,
			showAdditions: true,
			noListStyling: true,
			specificitiesProvider: ({item}) =>
			{
				let items = [];

				item.specificities.forEach(allocation =>
				{
					const specificityType = allocation.specificityType;

					items.push({
						label: specificityType.id !== LANGUAGE_MOTHER
							? allocation.specificityType.label
							: undefined,
						value: allocation.specificity.label,
					});
				});

				return items;
			},
			inlineProvider: (data) => isEmptyArray(data)
		}
	},
	[Inquiry.EDUCATION]: {
		fanTitle: 'Wird eine bestimmte Ausbildung für den Job benötigt?',
		dataProvider: ({jobProfileData}) => jobProfileData.education,
	},
	[Inquiry.INCOGNITO_MODE]: {
		dataProvider: ({jobProfileData}) => jobProfileData.incognitoMode,
	},
};

export default {

	components: {
		JonMessages,
		Jon,
		Button,
		Label,
		LockInfo,
		IncognitoMode,
		FunctionDetail,
		HomeOffice,
		Group,
		PopUp,
		Education,
		TravelWillingness,
		HourlyRate,
		Salary,
		EmploymentDuration,
		WorkingHours,
		HiringDate,
		SelectedItems,
		Title,
		Icon,
		Navigation,
		PlaceOfWork,
	},

	props: {
		jobProfileListItem64: {
			type: String,
			required: true,
		},
	},

	mounted ()
	{
		this.jobProfileData = prepare(this.jobProfileListItem.jobProfile, this.meta);

		if (this.inReview)
		{
			this.showReviewIntro = true;
		}
	},

	data ()
	{
		const jobProfileListItem = JSON.parse(
			window.atob(this.jobProfileListItem64)
		);
		const jobProfile = jobProfileListItem.jobProfile;

		return {
			jobProfileListItem,
			jobProfileData: undefined,
			reloadingJobProfile: false,
			editName: false,
			jobProfileName: jobProfile.name,
			jobProfileNameToEdit: jobProfile.name,
			changingName: false,
			saving: false,
			confirming: false,
			showReviewIntro: false
		};
	},

	computed: {
		...mapState('meta', ['meta']),
		...mapState('auth', ['customer', 'internal']),
		reviewIntroMessages ()
		{
			return [
				'Schön, dass du hier bist.',
				'Wir haben alle Informationen zum Job erfasst, die aus den vorhandenen Unterlagen ersichtlich waren (zB Inserate oder Stellenprofile) oder mitgeteilt wurden.',
				'Bitte ergänze oder ändere noch rasch und einfach die Punkte, die wir nicht kannten oder bei denen wir unsicher waren.',
				'So können dir dann passende Personen angezeigt werden.'
			];
		},
		inReview ()
		{
			return this.jobProfileListItem.inReview;
		},
		changeNameDataValid ()
		{
			return !isEmptyOrNull(this.jobProfileName);
		},
		selectedItemsStylingList ()
		{
			return STYLING__LIST;
		},
		selectedItemsConfigs ()
		{
			const items = _.filter(CONFIGS, item => isDefined(item.selectedItemsConfig));

			return _.map(items, item =>
			{
				return {
					inquiryId: _.findKey(CONFIGS, i => i === item),
					config: item.selectedItemsConfig
				};
			});
		},
		labelTypeJobProfile ()
		{
			return JOB_PROFILE;
		},
		jobProfile ()
		{
			return this.jobProfileListItem.jobProfile;
		},
		groupedItems ()
		{
			let grouped = {};

			_.forEach(this.jobProfileListItem.editableInquiries, inquiry =>
			{
				// only non-editable inquiries in review
				if ((this.inReview && !this.internal) && this.isInquiryEditable(inquiry))
				{
					return true; // continue
				}

				const area = inquiry.area;

				if (!grouped[area.id])
				{
					grouped[area.id] = {
						area,
						inquiries: [],
					};
				}

				grouped[area.id].inquiries.push(inquiry);
			});

			return _.orderBy(grouped, 'area.sort');
		},
		canConfirm ()
		{
			return this.getGroupCompletionType(_.first(this.groupedItems)) === COMPLETION_TYPE__COMPLETED;
		},
	},

	methods: {
		...mapActions('jobProfile/edit', {
			'openEditFan': 'show'
		}),
		...mapActions('characteristic/fan', {
			'openCharacteristicEditFan': 'show'
		}),
		getNonEditableItems (area)
		{
			return this.jobProfileListItem.editableInquiries
				.filter(inquiry =>
				{
					return inquiry.area.id === area.id && !this.isInquiryEditable(inquiry);
				})
				.map(inquiry =>
				{
					return {
						inquiry,
						config: CONFIGS[inquiry.id]
					}
				});
		},
		isSelectedItemsInline (selectedItemsConfig, data)
		{
			if (selectedItemsConfig.inlineProvider)
			{
				return selectedItemsConfig.inlineProvider(data);
			}

			return isEmptyArray(data) || itemsCountIs(data, 1);
		},
		filterLastLevel (items)
		{
			if (!items)
			{
				return items;
			}

			return filterLastLevel(items);
		},
		getInquiryLabel (inquiry)
		{
			return inquiry.label;
		},
		isInquiryEditable (inquiry)
		{
			if (this.internal)
			{
				return true;
			}

			const config = CONFIGS[inquiry.id];

			return config.editable !== false;
		},
		getInquiryCompletionType (inquiry)
		{
			return this.isInquiryCompleted(inquiry)
				? COMPLETION_TYPE__COMPLETED
				: COMPLETION_TYPE__INCOMPLETE;
		},
		isInquiryCompleted (inquiry)
		{
			return _.includes(this.jobProfileListItem.completedInquiries, inquiry.id);
		},
		getGroupCompletionType (group)
		{
			const inquiries = group.inquiries;

			const completedInquiries = _.filter(inquiries, inquiry => this.isInquiryCompleted(inquiry));

			return completedInquiries.length === inquiries.length
				? COMPLETION_TYPE__COMPLETED
				: COMPLETION_TYPE__INCOMPLETE;
		},
		edit (inquiry)
		{
			const config = CONFIGS[inquiry.id];

			if (!config)
			{
				return;
			}

			const data = config.dataProvider({
				jobProfileData: this.jobProfileData,
				meta: this.meta
			});

			if (config.characteristicEditFanConfigProvider)
			{
				const characteristicEditFanConfig = config.characteristicEditFanConfigProvider({
					meta: this.meta
				});

				this.openCharacteristicEditFan({
					selected: _.cloneDeep(data),
					characteristicType: characteristicEditFanConfig.characteristicType,
					characteristicItems: characteristicEditFanConfig.characteristicItems,
					title: characteristicEditFanConfig.title,
					subTitle: characteristicEditFanConfig.subTitle,
					nothingAllowed: characteristicEditFanConfig.nothingAllowed,
					selectedItemsConfig: characteristicEditFanConfig.selectedItemsConfig,
					experienceConfig: characteristicEditFanConfig.experienceConfig,
					additionsConfig: characteristicEditFanConfig.additionsConfig,
					withKeyword: characteristicEditFanConfig.withKeyword,
					withExperience: characteristicEditFanConfig.withExperience,
					searchConfig: characteristicEditFanConfig.searchConfig,
					supplementsConfig: characteristicEditFanConfig.supplementsConfig,
					labelType: JOB_PROFILE,
					labelSet: 'customer',
					onChange: async ({selected}) =>
					{
						this.saving = true;

						try
						{
							const preparedData = PreSavePreparation.prepare({
								[characteristicEditFanConfig.property]: selected
							});

							await new JobProfileSave()
								.setJobProfileId(this.jobProfile.id)
								.setData(preparedData)
								.call();

							Toast.success(this, ['Erfolgreich gespeichert']);

							await this.reloadJobProfile();
						}
						catch (e)
						{
							await Log.error(e);
							Toast.error(this, flatten(e.errors));
						}

						this.saving = false;
					}
				});
			}
			else
			{
				let fanTitle = config.fanTitle;

				if (typeof fanTitle === 'function')
				{
					fanTitle = fanTitle({ customer: this.customer });
				}

				this.openEditFan({
					title: fanTitle,
					subTitle: config.fanSubTitle,
					jobProfile: this.jobProfileListItem,
					initialData: _.cloneDeep(data),
					inquiryToEdit: inquiry,
					onSave: async ({confirmed}) =>
					{
						if (!confirmed)
						{
							return;
						}

						this.reloadJobProfile();
					}
				});
			}
		},
		async reloadJobProfile ()
		{
			this.reloadingJobProfile = true;

			const response = await new GetList()
				.setId(this.jobProfile.id)
				.call();

			this.jobProfileListItem = _.first(response.data);
			this.jobProfileData = prepare(this.jobProfileListItem.jobProfile, this.meta);

			this.reloadingJobProfile = false;
		},
		onAbortNameChange ()
		{
			this.jobProfileNameToEdit = this.jobProfileName;
			this.editName = false;
		},
		async saveName ()
		{
			this.changingName = true;

			try
			{
				await new Save()
					.setJobProfileId(this.jobProfile.id)
					.setData({
						name: this.jobProfileNameToEdit
					})
					.call();

				this.jobProfileName = this.jobProfileNameToEdit;

				this.editName = false;
			}
			catch (e)
			{
				Toast.error(this, flatten(e.errors));
			}

			this.changingName = false;
		},
		async confirm ()
		{
			this.confirming = true;

			try
			{
				await new Save()
					.setJobProfileId(this.jobProfile.id)
					.setData({
						reviewConfirmation: true
					})
					.call();

				redirect('/kundenbereich/jobs/' + this.jobProfile.id);
			}
			catch (e)
			{
				Toast.error(this, flatten(e.errors));
			}
		}
	}
}
</script>
